import { HubConnectionState } from '@microsoft/signalr';
import AppContext, { IAppContext } from 'App/AppContext';
import { createContext, FC, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import logger from 'services/Logging/logService';
import { IHubConnection, stopHubConnection } from 'services/SignalR/GeneralHub';
import { ITaskViewEvent, requestTaskViewReplay, startTaskViewHubConnection } from 'services/SignalR/TaskViewHub';
import { logTaskViewDiagnostic } from 'utils/taskViewDiagnostics';
import { canUseTaskViews } from './TaskViewAuthorization';

export type TaskViewHubConnectionState = 'connecting' | 'connected' | 'reconnecting' | 'disconnected';

export type TaskViewEventHandler = (evt: ITaskViewEvent) => void;
export type TaskViewResyncHandler = () => Promise<void>;

interface ITaskViewHubContextValue {
  connectionState: TaskViewHubConnectionState;
  subscribe: (onEvent: TaskViewEventHandler, onResync: TaskViewResyncHandler) => () => void;
}

const noopSubscribe: ITaskViewHubContextValue['subscribe'] = () => () => undefined;

const TaskViewHubContext = createContext<ITaskViewHubContextValue>({
  connectionState: 'disconnected',
  subscribe: noopSubscribe,
});

export const useTaskViewHub = (): ITaskViewHubContextValue => useContext(TaskViewHubContext);

interface ISubscriberState {
  onEvent: TaskViewEventHandler;
  onResync: TaskViewResyncHandler;
  buffer: ITaskViewEvent[];
  syncing: boolean;
  // Set when the buffer overflowed during a resync. The drain step is
  // skipped and a follow-up resync is fired instead -- the refetch IS the
  // canonical state, so dropped events are recovered as part of that snapshot.
  needsResync: boolean;
}

const initialRestartDelayMs = 5000;
const restartDelayCapMs = 5 * 60 * 1000;
// Cap the per-subscriber event buffer that accumulates during a resync.
// At 200 events the refetch is clearly losing the race; replaying that many
// stale events on top of a fresh snapshot is worse than just refetching again.
const maxBufferSize = 200;

interface IProps {
  children?: ReactNode;
}

// Connects to the TaskView SignalR hub for the lifetime of the children tree.
// Mounted app-wide (see AppRouting) -- not just over the Tasks scene -- because
// the sidebar (NavMain) consumes TaskView state everywhere and needs the same
// live cross-tab sync. The trade-off: every authenticated user holds one idle
// TaskView WebSocket. The provider owns reconnect + tab-visibility recovery;
// consumers subscribe with (onEvent, onResync) and the provider invokes
// onResync on initial subscribe, on automatic reconnect, and on tab visibility
// resume so consumers' local state never drifts silently.
export const TaskViewHubProvider: FC<IProps> = ({ children }) => {
  // AppContext.Provider passes its class component's `this.state` unmemoized,
  // so a new reference flows to consumers on every setState in
  // AppContextProvider (org switch, content-loader toggle, anything). Keeping
  // appContext in this effect's dep array tore the WebSocket down on every
  // unrelated state cascade. Hold it in a ref so the connect effect runs once
  // for the lifetime of the Provider and start() reads the live value at
  // connect time -- including the latest getAccessToken closure.
  const appContext = useContext(AppContext);
  const appContextRef = useRef<IAppContext>(appContext);
  appContextRef.current = appContext;
  const taskViewsAvailable = canUseTaskViews(appContext);
  const [connectionState, setConnectionState] = useState<TaskViewHubConnectionState>('connecting');
  const hubRef = useRef<IHubConnection | null>(null);
  const subscribersRef = useRef<Set<ISubscriberState>>(new Set());
  // In-memory monotonic sequence cursor for the server's event outbox. Held in
  // memory only -- never persisted: a replay cursor acknowledges events this
  // live connection has processed, so a fresh page load (or org switch) starts
  // at 0, the server replies snapshotRequired, and subscribers do a full
  // resync. The cursor is reset to 0 at the top of the connect effect below.
  const lastSequenceRef = useRef<number>(0);

  // Drains the buffer for a subscriber after its resync completes. Events
  // arriving DURING the resync are applied in arrival order AFTER the
  // refetch lands -- the refetch is treated as a T0 snapshot and the buffer
  // as T0+ deltas. Closes the event-during-refetch race. If the buffer
  // overflowed while we were resyncing, skip the replay and fire one more
  // resync -- the next refetch's snapshot already incorporates everything
  // that was dropped.
  const triggerResync = useCallback(async function run(sub: ISubscriberState): Promise<void> {
    logTaskViewDiagnostic('hub.triggerResync.start', {
      bufferCount: sub.buffer.length,
      needsResync: sub.needsResync,
    });
    sub.syncing = true;
    try {
      // Events can arrive while this refetch is running. Check needsResync
      // after the await so an overflow during the fetch triggers one more
      // canonical snapshot instead of replaying a truncated buffer.
      await sub.onResync();
      if (sub.needsResync) {
        sub.needsResync = false;
        sub.buffer.length = 0;
        sub.syncing = false;
        logTaskViewDiagnostic('hub.triggerResync.repeatAfterOverflow');
        await run(sub);

        return;
      }
      const drained = sub.buffer.splice(0);
      logTaskViewDiagnostic('hub.triggerResync.drain', {
        drainedCount: drained.length,
      });
      drained.forEach((evt) => {
        try {
          sub.onEvent(evt);
        } catch (err) {
          logger.debug('TaskViewHubProvider: subscriber onEvent threw during drain', err);
        }
      });
    } catch (err) {
      logger.debug('TaskViewHubProvider: subscriber onResync failed', err);
      logTaskViewDiagnostic('hub.triggerResync.error', {
        code: (err as { code?: string })?.code,
      });
    } finally {
      sub.syncing = false;
      logTaskViewDiagnostic('hub.triggerResync.done');
    }
  }, []);

  const dispatchToSubscribers = useCallback((evt: ITaskViewEvent) => {
    logTaskViewDiagnostic('hub.dispatch.event', {
      kind: evt.kind,
      sequenceNumber: evt.sequenceNumber,
      lastSequence: lastSequenceRef.current,
      subscriberCount: subscribersRef.current.size,
    });
    // Stale-event guard -- replay + a live event can race and deliver the
    // same sequence twice. Drop anything we have already observed.
    if (evt.sequenceNumber <= lastSequenceRef.current) {
      logTaskViewDiagnostic('hub.dispatch.dropStale', {
        kind: evt.kind,
        sequenceNumber: evt.sequenceNumber,
        lastSequence: lastSequenceRef.current,
      });

      return;
    }
    lastSequenceRef.current = evt.sequenceNumber;

    subscribersRef.current.forEach((sub) => {
      if (sub.syncing) {
        if (sub.buffer.length >= maxBufferSize) {
          // Overflow: flag a follow-up resync; stop accumulating. triggerResync
          // checks the flag after its current await resolves and re-runs.
          sub.needsResync = true;
          logger.debug('TaskViewHubProvider: event buffer overflowed, queued a re-resync');
          logTaskViewDiagnostic('hub.dispatch.bufferOverflow', {
            kind: evt.kind,
            sequenceNumber: evt.sequenceNumber,
            bufferCount: sub.buffer.length,
          });

          return;
        }
        sub.buffer.push(evt);
        logTaskViewDiagnostic('hub.dispatch.bufferedDuringSync', {
          kind: evt.kind,
          sequenceNumber: evt.sequenceNumber,
          bufferCount: sub.buffer.length,
        });

        return;
      }
      try {
        sub.onEvent(evt);
      } catch (err) {
        // Isolate subscribers from each other -- a throw here would otherwise
        // skip every subscriber after this one in the forEach loop.
        logger.debug('TaskViewHubProvider: subscriber onEvent threw', err);
      }
    });
  }, []);

  useEffect(() => {
    let active = true;
    let restartTimer: number | null = null;
    let restartDelayMs = initialRestartDelayMs;
    let currentHub: IHubConnection | null = null;

    // This effect re-runs when the tenant id resolves or changes (org switch).
    // Reset the in-memory cursor so a new tenant starts a fresh outbox cursor
    // rather than replaying against the previous tenant's sequence.
    lastSequenceRef.current = 0;
    logTaskViewDiagnostic('hub.effect.start', {
      tenantId: appContext.user?.login?.tenantId,
      taskViewsAvailable,
    });

    // Cold-mount gate. The hub URL bakes in the tenant id, but a tenant alone
    // is not enough: app-wide providers mount before route-level authorization.
    if (!taskViewsAvailable) {
      setConnectionState('disconnected');
      logTaskViewDiagnostic('hub.effect.skipNotAuthorized', {
        isAuthenticated: appContext.isAuthenticated,
        isAuthInProgress: appContext.isAuthInProgress,
        tenantId: appContext.user?.login?.tenantId,
      });

      return;
    }

    const scheduleRestart = (start: () => Promise<void>) => {
      if (!active || restartTimer !== null) return;
      logTaskViewDiagnostic('hub.restart.schedule', { restartDelayMs });
      restartTimer = window.setTimeout(() => {
        restartTimer = null;
        restartDelayMs = Math.min(restartDelayMs * 2, restartDelayCapMs);
        void start();
      }, restartDelayMs);
    };

    const resyncEveryone = () => {
      logTaskViewDiagnostic('hub.resyncEveryone', {
        subscriberCount: subscribersRef.current.size,
      });
      subscribersRef.current.forEach((sub) => {
        void triggerResync(sub);
      });
    };

    // Ask the server for any events appended for this user since the last
    // sequence we observed. If the gap exceeds the buffer's retention window
    // the server signals snapshotRequired and we leave the catch-up to the
    // subscribers' onResync handlers (full refetch).
    const replayMissedEvents = async (conn: IHubConnection): Promise<void> => {
      const lastSeen = lastSequenceRef.current;
      logTaskViewDiagnostic('hub.replay.start', { lastSeen });
      const result = await requestTaskViewReplay(conn, lastSeen);
      if (!active || result === null) {
        logTaskViewDiagnostic('hub.replay.noResult', { active });

        return;
      }
      logTaskViewDiagnostic('hub.replay.result', {
        snapshotRequired: result.snapshotRequired,
        eventCount: result.events.length,
        currentSequence: result.currentSequence,
      });
      if (result.snapshotRequired) {
        // Snapshot path -- discard the local sequence so we trust the next
        // live event regardless of order; the refetch covers state.
        lastSequenceRef.current = result.currentSequence;

        return;
      }
      // Replay events through the normal dispatch path. The stale-event
      // guard inside dispatchToSubscribers protects against duplicate
      // delivery if a live event arrives between RequestReplay returning
      // and the next ReceiveTaskViewEvent invocation.
      result.events.forEach((evt) => dispatchToSubscribers(evt));
    };

    const start = async (): Promise<void> => {
      if (!active) return;
      logTaskViewDiagnostic('hub.connection.start');
      setConnectionState('connecting');
      const conn = await startTaskViewHubConnection(appContextRef.current, dispatchToSubscribers);
      if (!active) {
        logTaskViewDiagnostic('hub.connection.closeInactiveStartResult');
        if (conn) await stopHubConnection(conn);

        return;
      }
      if (!conn) {
        setConnectionState('disconnected');
        logTaskViewDiagnostic('hub.connection.failed');
        scheduleRestart(start);

        return;
      }

      currentHub = conn;
      hubRef.current = conn;
      setConnectionState('connected');
      logTaskViewDiagnostic('hub.connection.connected');
      restartDelayMs = initialRestartDelayMs;

      conn.connection.onreconnecting(() => {
        if (!active) return;
        setConnectionState('reconnecting');
        logTaskViewDiagnostic('hub.connection.reconnecting');
      });
      conn.connection.onreconnected(() => {
        if (!active) return;
        setConnectionState('connected');
        logTaskViewDiagnostic('hub.connection.reconnected');
        // Try a sequence-based replay first; subscribers still get an
        // onResync as belt-and-braces in case the server has rotated the
        // buffer (snapshotRequired) or the replay fails.
        void replayMissedEvents(conn).finally(() => {
          if (active) resyncEveryone();
        });
      });
      conn.connection.onclose(() => {
        if (!active) return;
        if (currentHub === conn) {
          currentHub = null;
        }
        if (hubRef.current === conn) {
          hubRef.current = null;
        }
        setConnectionState('disconnected');
        logTaskViewDiagnostic('hub.connection.closed');
        scheduleRestart(start);
      });

      // Initial connect: replay anything appended since the in-memory cursor
      // (events between connects within this session), then resync. On a fresh
      // page load the cursor is 0 -> the server replies snapshotRequired and the
      // resync's full refetch covers state.
      void replayMissedEvents(conn).finally(() => {
        if (active) resyncEveryone();
      });
    };

    const onVisibilityChange = () => {
      if (!active || document.visibilityState !== 'visible') return;
      logTaskViewDiagnostic('hub.visibility.visible', {
        connectionState: hubRef.current?.connection.state,
      });
      const current = hubRef.current;
      if (!current || current.connection.state !== HubConnectionState.Connected) {
        scheduleRestart(start);

        return;
      }
      resyncEveryone();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    void start();

    return () => {
      active = false;
      logTaskViewDiagnostic('hub.effect.cleanup');
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (restartTimer !== null) window.clearTimeout(restartTimer);
      const hubToStop = currentHub;
      currentHub = null;
      if (hubRef.current === hubToStop) {
        hubRef.current = null;
      }
      void stopHubConnection(hubToStop);
    };
    // Keep the reconnect boundary narrow: tenant changes need a fresh URL, and
    // auth readiness changes must close/open the app-wide socket.
  }, [
    appContext.user?.login?.tenantId,
    appContext.isAuthenticated,
    appContext.isAuthInProgress,
    taskViewsAvailable,
    dispatchToSubscribers,
    triggerResync,
  ]);

  const subscribe = useCallback<ITaskViewHubContextValue['subscribe']>(
    (onEvent, onResync) => {
      const sub: ISubscriberState = { onEvent, onResync, buffer: [], syncing: false, needsResync: false };
      subscribersRef.current.add(sub);
      logTaskViewDiagnostic('hub.subscribe', {
        subscriberCount: subscribersRef.current.size,
        connectionState: hubRef.current?.connection.state,
      });
      // Treat initial subscribe like a reconnect -- the consumer might have
      // missed events between hub-connect and effect-register. If the hub is
      // still connecting, the connect path already replays + resyncs everyone;
      // firing here would duplicate the initial layout GET.
      if (hubRef.current?.connection.state === HubConnectionState.Connected) {
        void triggerResync(sub);
      } else {
        logTaskViewDiagnostic('hub.subscribe.deferInitialResyncUntilConnected');
      }

      return () => {
        subscribersRef.current.delete(sub);
        logTaskViewDiagnostic('hub.unsubscribe', {
          subscriberCount: subscribersRef.current.size,
        });
      };
    },
    [triggerResync],
  );

  const value = useMemo<ITaskViewHubContextValue>(() => ({ connectionState, subscribe }), [connectionState, subscribe]);

  return <TaskViewHubContext.Provider value={value}>{children}</TaskViewHubContext.Provider>;
};
